import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "universal-cookie";
import { FaSearch, FaUsers, FaTimes, FaUserFriends, FaIdCard, FaLink, FaFileAlt } from "react-icons/fa";
import "../../css/Suscriptores.css";

const baseURL = process.env.REACT_APP_API_BASE_URL;
const API = `${baseURL}/api/Suscriptores`;

const Suscriptores = () => {
  const cookies = new Cookies();

  // ── Estado ──────────────────────────────────────────────────────────────────
  const [datos, setDatos] = useState([]);
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [filtroPlan, setFiltroPlan] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [paginaActual, setPaginaActual] = useState(1);
  const [loading, setLoading] = useState(false);
  const [planesUnicos, setPlanesUnicos] = useState([]);

  // Modal beneficiarios
  const [modalBenef, setModalBenef] = useState(false);
  const [suscriptorActivo, setSuscriptorActivo] = useState(null);
  const [beneficiarios, setBeneficiarios] = useState([]);
  const [loadingBenef, setLoadingBenef] = useState(false);

  // Modal documentos
  const [modalDocs, setModalDocs] = useState(false);
  const [documentos, setDocumentos] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [suscDocActivo, setSuscDocActivo] = useState(null);

  const elementosPorPagina = 10;

  // ── Fetch suscriptores ───────────────────────────────────────────────────────
  const fetchSuscriptores = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ObtenerSuscriptores`);
      const lista = res.data?.data || res.data || [];
      setDatos(lista);
      const planes = [...new Set(lista.map((s) => s.nombre_plan).filter(Boolean))];
      setPlanesUnicos(planes);
      setPaginaActual(1);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", title: "Error al cargar suscriptores", text: "Intenta nuevamente." });
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Filtrado reactivo ────────────────────────────────────────────────────────
  useEffect(() => {
    const q = busqueda.trim().toLowerCase();
    const filtrados = datos.filter((s) => {
      const coincideBusqueda =
        !q ||
        s.nombresApellidos?.toLowerCase().includes(q) ||
        s.documento?.toLowerCase().includes(q) ||
        s.celular?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q);
      const coincidePlan = !filtroPlan || s.nombre_plan === filtroPlan;
      const coincideEstado = !filtroEstado || s.estado_suscripcion === filtroEstado;
      return coincideBusqueda && coincidePlan && coincideEstado;
    });
    setDatosFiltrados(filtrados);
    setPaginaActual(1);
  }, [datos, busqueda, filtroPlan, filtroEstado]);

  useEffect(() => { fetchSuscriptores(); }, [fetchSuscriptores]);

  // ── Beneficiarios ────────────────────────────────────────────────────────────
  const verBeneficiarios = async (suscriptor) => {
    setSuscriptorActivo(suscriptor);
    setBeneficiarios([]);
    setModalBenef(true);
    setLoadingBenef(true);
    try {
      const res = await axios.get(`${API}/ObtenerBeneficiarios`, {
        params: { suscriptor_id: suscriptor.id_suscriptor }
      });
      setBeneficiarios(res.data?.data || res.data || []);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", title: "Error al cargar beneficiarios", text: "Intenta nuevamente." });
    } finally {
      setLoadingBenef(false);
    }
  };

  // ── Enviar link de pago ──────────────────────────────────────────────────────
  const enviarLinkPago = async (suscriptor) => {
    const confirmacion = await Swal.fire({
      title: "¿Obtener link de pago?",
      html: `<b>${suscriptor.nombresApellidos}</b><br/>
             <span style="color:#6c757d;font-size:0.9em">${suscriptor.celular} / ${suscriptor.email}</span>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, obtener",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1a56db",
    });
    if (!confirmacion.isConfirmed) return;

    try {
      const res = await axios.post(`${API}/EnviarLinkPago`, {
        id_suscriptor: suscriptor.id_suscriptor,
        id_suscripcion: suscriptor.id_suscripcion,
      });

      const { tipo_pago, url_pago } = res.data;

      // Limpiar celular para WhatsApp (solo dígitos, agregar 57 si no tiene código)
      const enviarWhatsApp = async () => {
        try {
          const asesorWeb = cookies.get("nombreUsuario") || "";
          await axios.post(`${baseURL}/api/Chat/EnviarMensajeWhatsApp`, {
            asesorWeb: asesorWeb,
            telefono: suscriptor.celular,
            mensaje: `Hola ${suscriptor.nombresApellidos}. Tu proceso de afiliación está casi listo. Para finalizar, realiza tu pago en el siguiente enlace: ${url_pago}`
          });
          Swal.fire({
            toast: true, position: "top-end", icon: "success",
            title: `WhatsApp enviado a ${suscriptor.celular}`,
            showConfirmButton: false, timer: 3500, timerProgressBar: true,
          });
        } catch (err) {
          const msg = err.response?.data?.mensaje || "Error al enviar el mensaje de WhatsApp.";
          Swal.fire({ icon: "error", title: "Error", text: msg });
        }
      };
      const enviarCorreo = async () => {
        try {
          await axios.post(`${API}/EnviarCorreoLinkPago`, {
            id: suscriptor.id_suscriptor,
            correo: suscriptor.email,
            nombres: suscriptor.nombresApellidos,
            nroDocumento: suscriptor.documento,
            linkPago: url_pago,
            nombrePlan: suscriptor.nombre_plan,
          });
          Swal.fire({
            toast: true, position: "top-end", icon: "success",
            title: `Correo enviado a ${suscriptor.email}`,
            showConfirmButton: false, timer: 3500, timerProgressBar: true,
          });
        } catch (err) {
          const msg = err.response?.data?.mensaje || "Error al enviar el correo.";
          Swal.fire({ icon: "error", title: "Error", text: msg });
        }
      };

      await Swal.fire({
        title: tipo_pago === "PSE" ? "🏦 Link PSE" : "🔗 Link de pago",
        html: `
          <p style="font-size:0.88rem;color:#6c757d;margin-bottom:12px">
            ${tipo_pago === "PSE"
            ? "Pago PSE en curso. Link de redirección:"
            : "Link generado. Compártelo con el suscriptor:"}
          </p>

          <!-- URL + copiar -->
          <div style="display:flex;align-items:center;gap:8px;background:#f8f9fa;border:1px solid #dee2e6;border-radius:8px;padding:10px 14px;margin-bottom:16px">
            <span style="font-size:0.75rem;color:#495057;word-break:break-all;flex:1;text-align:left">${url_pago}</span>
            <button id="btnCopiar" style="flex-shrink:0;background:#1a56db;color:#fff;border:none;border-radius:6px;padding:5px 12px;font-size:0.78rem;cursor:pointer;white-space:nowrap">
              Copiar
            </button>
          </div>

          <!-- Acciones de envío -->
          <p style="font-size:0.78rem;color:#adb5bd;margin-bottom:10px;text-transform:uppercase;letter-spacing:0.5px;font-weight:600">Enviar por</p>
          <div style="display:flex;gap:10px;justify-content:center">
            <button id="btnWhatsApp" style="display:inline-flex;align-items:center;gap:8px;background:#25D366;color:#fff;border:none;border-radius:8px;padding:9px 20px;font-size:0.85rem;font-weight:600;cursor:pointer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </button>
            <button id="btnCorreo" style="display:inline-flex;align-items:center;gap:8px;background:#e8f0fe;color:#1a56db;border:1px solid #bfdbfe;border-radius:8px;padding:9px 20px;font-size:0.85rem;font-weight:600;cursor:pointer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a56db" stroke-width="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m2 7 10 7 10-7"/></svg>
              Correo
            </button>
          </div>`,
        showConfirmButton: false,
        showCancelButton: true,
        cancelButtonText: "Cerrar",
        cancelButtonColor: "#6c757d",
        didOpen: () => {
          document.getElementById("btnCopiar")?.addEventListener("click", () => {
            navigator.clipboard.writeText(url_pago).then(() => {
              const btn = document.getElementById("btnCopiar");
              if (btn) { btn.textContent = "✓ Copiado"; btn.style.background = "#28a745"; }
            });
          });
          document.getElementById("btnWhatsApp")?.addEventListener("click", () => {
            enviarWhatsApp();
          });
          document.getElementById("btnCorreo")?.addEventListener("click", () => {
            enviarCorreo();
          });
        },
      });

    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.mensaje || "Intenta nuevamente.";
      Swal.fire({ icon: "error", title: "Error al obtener link de pago", text: msg });
    }
  };

  // ── Ver documentos ───────────────────────────────────────────────────────────
  const verDocumentos = async (suscriptor) => {
    setSuscDocActivo(suscriptor);
    setDocumentos([]);
    setModalDocs(true);
    setLoadingDocs(true);
    try {
      const res = await axios.get(`${API}/ObtenerDocumentos`, {
        params: { suscriptor_id: suscriptor.id_suscriptor }
      });
      setDocumentos(res.data?.data || res.data || []);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", title: "Error al cargar documentos", text: "Intenta nuevamente." });
    } finally {
      setLoadingDocs(false);
    }
  };


  // ── Generar pago efectivo anual ──────────────────────────────────────────────
  const generarPagoEfectivoAnual = async (suscriptor) => {
    // Primero obtener el valor del plan anual
    let valorPlan = null;
    try {
      const res = await axios.get(`${API}/ObtenerValorPlan`, {
        params: { planId: suscriptor.plan_id }  // ← cambia id_plan por plan_id
      });
      valorPlan = res.data?.precioAnual ?? res.data?.data?.precioAnual;
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo obtener el valor del plan." });
      return;
    }

    const valorFormateado = new Intl.NumberFormat("es-CO", {
      style: "currency", currency: "COP", minimumFractionDigits: 0
    }).format(valorPlan);

    const confirmacion = await Swal.fire({
  title: "💰 Pago anual efectivo",
  html: `
    <p style="font-size:0.95rem;color:#374151;margin-bottom:8px">
      ${suscriptor.frecuencia_pago?.toUpperCase() === "MENSUAL"
        ? "La frecuencia pasará de <b>MENSUAL</b> a <b>ANUAL</b>."
        : "Se generará un <b>pago anual en efectivo</b> para este suscriptor."
      }
    </p>
    <p style="font-size:0.9rem;color:#6c757d;margin-bottom:12px">
      Plan: <b>${suscriptor.nombre_plan}</b>
    </p>
    <div style="background:#f0fdf4;border:1px solid #86efac;border-radius:10px;padding:12px 20px;display:inline-block">
      <span style="font-size:0.82rem;color:#166534;font-weight:600;text-transform:uppercase;letter-spacing:0.5px">Costo anual</span>
      <div style="font-size:1.6rem;font-weight:700;color:#15803d">${valorFormateado}</div>
    </div>
    <p style="font-size:0.82rem;color:#9ca3af;margin-top:12px">
      ¿Desea continuar con el pago anual?
    </p>`,
  icon: "question",
  showCancelButton: true,
  confirmButtonText: "Sí, continuar",
  cancelButtonText: "Cancelar",
  confirmButtonColor: "#15803d",
});

    if (!confirmacion.isConfirmed) return;

    try {
      const usuarioRegistra = cookies.get("idUsuario") || 1;
      await axios.post(`${API}/GenerarPagoEfectivoAnual`, null, {
        params: {
          suscripcionId: suscriptor.id_suscripcion,
          usuarioRegistra: usuarioRegistra,
        }
      });
      Swal.fire({
        toast: true, position: "top-end", icon: "success",
        title: "Pago anual generado exitosamente",
        showConfirmButton: false, timer: 3500, timerProgressBar: true,
      });
      fetchSuscriptores(); // Refresca la grilla
    } catch (err) {
      const msg = err.response?.data?.mensaje || "Error al generar el pago anual.";
      Swal.fire({ icon: "error", title: "Error", text: msg });
    }
  };


  // ── Helpers ──────────────────────────────────────────────────────────────────
  const esPlanFamiliar = (plan) => plan?.toLowerCase().includes("famil");
  const esSinPago = (estado) => estado?.toUpperCase() === "SIN PAGO";

  const esSinPagoMensual = (s) => esSinPago(s.estado_suscripcion);


  const badgeEstado = (estado) => {
    const e = estado?.toUpperCase();
    if (e === "ACTIVA") return "badge-activo";
    if (e === "INACTIVA") return "badge-inactivo";
    if (e === "SIN PAGO") return "badge-sinpago";
    return "badge-inactivo";
  };

  const badgeFrecuencia = (freq) => {
    const f = freq?.toUpperCase();
    if (f === "MENSUAL") return "badge-mensual";
    if (f === "ANUAL") return "badge-anual";
    if (f === "SEMESTRAL") return "badge-semestral";
    return "badge-frecuencia";
  };

  // ── Paginación ───────────────────────────────────────────────────────────────
  const indexInicio = (paginaActual - 1) * elementosPorPagina;
  const indexFin = indexInicio + elementosPorPagina;
  const datosPaginados = datosFiltrados.slice(indexInicio, indexFin);
  const totalPaginas = Math.ceil(datosFiltrados.length / elementosPorPagina);
  const rangoInicio = Math.max(1, Math.min(paginaActual - 2, totalPaginas - 4));
  const rangoFin = Math.min(totalPaginas, rangoInicio + 4);
  const paginasVisibles = Array.from({ length: rangoFin - rangoInicio + 1 }, (_, i) => rangoInicio + i);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="telefonos-container">
      <div className="telefonos-card">

        {/* Header */}
        <div className="card-header-custom d-flex align-items-center justify-content-between">
          <h3 className="card-title-custom">
            <FaUsers style={{ marginRight: 10 }} />
            Suscriptores
          </h3>
          <button className="btn-refresh-susc" onClick={fetchSuscriptores} title="Actualizar">
            <i className="fas fa-sync-alt" style={{ marginRight: 6 }} />
            Actualizar
          </button>
        </div>

        <div className="form-section">

          {/* ── Filtros ── */}
          <div className="suscriptores-filtros-wrap">
            <div className="usuarios-search-wrap">
              <FaSearch className="usuarios-search-icon" />
              <input
                type="text"
                className="custom-input usuarios-search-input"
                placeholder="Buscar por nombre, documento, celular o email..."
                autoComplete="off"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>

            <div className="usuarios-filter-item">
              <label className="usuarios-filter-label">Plan</label>
              <select className="custom-input" style={{ width: 170 }} value={filtroPlan} onChange={(e) => setFiltroPlan(e.target.value)}>
                <option value="">Todos</option>
                {planesUnicos.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            <div className="usuarios-filter-item">
              <label className="usuarios-filter-label">Estado</label>
              <select className="custom-input" style={{ width: 140 }} value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                <option value="">Todos</option>
                <option value="ACTIVA">Activa</option>
                <option value="INACTIVA">Inactiva</option>
                <option value="SIN PAGO">Sin Pago</option>
              </select>
            </div>

            <div className="usuarios-contador">
              <span className="usuarios-badge-count">
                {datosFiltrados.length} suscriptor{datosFiltrados.length !== 1 ? "es" : ""}
              </span>
            </div>
          </div>

          {/* ── Tabla con scroll horizontal ── */}
          <div className="suscriptores-table-scroll">
            <table className="usuarios-table suscriptores-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Documento</th>
                  <th>Nombres y Apellidos</th>
                  <th>Celular</th>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Suscripción</th>
                  <th>Frecuencia</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="sin-resultados">
                      <i className="fas fa-spinner fa-spin mr-2" /> Cargando suscriptores...
                    </td>
                  </tr>
                ) : datosPaginados.length > 0 ? (
                  datosPaginados.map((s, idx) => (
                    <tr key={s.id_suscriptor}>
                      <td className="td-id">{indexInicio + idx + 1}</td>
                      <td>
                        <span className="suscriptor-documento">
                          <FaIdCard size={12} style={{ marginRight: 5, opacity: 0.6 }} />
                          {s.documento}
                        </span>
                      </td>
                      <td><span className="suscriptor-nombre">{s.nombresApellidos}</span></td>
                      <td className="td-secondary">{s.celular}</td>
                      <td><span className="suscriptor-email">{s.email}</span></td>
                      <td>
                        <span className={`badge-plan ${esPlanFamiliar(s.nombre_plan) ? "badge-plan-familiar" : "badge-plan-basico"}`}>
                          {esPlanFamiliar(s.nombre_plan) && <FaUserFriends size={10} style={{ marginRight: 4 }} />}
                          {s.nombre_plan}
                        </span>
                      </td>
                      <td>
                        <span className={`badge-estado ${badgeEstado(s.estado_suscripcion)}`}>
                          <span className="badge-dot" />
                          {s.estado_suscripcion}
                        </span>
                      </td>
                      <td>
                        <span className={`badge-frecuencia-tag ${badgeFrecuencia(s.frecuencia_pago)}`}>
                          {s.frecuencia_pago}
                        </span>
                      </td>
                      <td>
                        <div className="acciones-wrap">
                          {/* Beneficiarios — solo plan familiar */}
                          {esPlanFamiliar(s.nombre_plan) && (
                            <button className="btn-icono btn-icono-benef" onClick={() => verBeneficiarios(s)} title="Ver beneficiarios">
                              <FaUserFriends size={14} />
                              <span className="icono-tooltip">Beneficiarios</span>
                            </button>
                          )}
                          {/* Documentos — siempre */}
                          <button className="btn-icono btn-icono-docs" onClick={() => verDocumentos(s)} title="Ver documentos">
                            <FaFileAlt size={14} />
                            <span className="icono-tooltip">Documentos</span>
                          </button>
                          {/* Link de pago — solo SIN PAGO */}
                          {esSinPago(s.estado_suscripcion) && (
                            <button className="btn-icono btn-icono-pago" onClick={() => enviarLinkPago(s)} title="Enviar link de pago">
                              <FaLink size={14} />
                              <span className="icono-tooltip">Link de pago</span>
                            </button>
                          )}

                          {/* Pago efectivo anual — solo SIN PAGO + MENSUAL */}
                          {esSinPagoMensual(s) && (
                            <button
                              className="btn-icono btn-icono-pago-anual"
                              onClick={() => generarPagoEfectivoAnual(s)}
                              title="Generar pago anual efectivo"
                            >
                              💰
                              <span className="icono-tooltip">Pago anual efectivo</span>
                            </button>
                          )}


                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="sin-resultados">
                      <i className="fas fa-search mr-2" />
                      No se encontraron suscriptores
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Paginación ── */}
          {totalPaginas > 1 && (
            <div className="fact-paginacion">
              <span className="fact-pag-info">
                Mostrando {indexInicio + 1}–{Math.min(indexFin, datosFiltrados.length)} de {datosFiltrados.length} suscriptores
              </span>
              <div className="paginacion-wrap">
                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === 1} onClick={() => setPaginaActual(1)}>«</button>
                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === 1} onClick={() => setPaginaActual(p => p - 1)}>‹</button>
                {rangoInicio > 1 && <span className="btn-pagina btn-pag-dots">…</span>}
                {paginasVisibles.map((n) => (
                  <button key={n} className={`btn-pagina ${paginaActual === n ? "activa" : ""}`} onClick={() => setPaginaActual(n)}>{n}</button>
                ))}
                {rangoFin < totalPaginas && <span className="btn-pagina btn-pag-dots">…</span>}
                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === totalPaginas} onClick={() => setPaginaActual(p => p + 1)}>›</button>
                <button className="btn-pagina btn-pag-nav" disabled={paginaActual === totalPaginas} onClick={() => setPaginaActual(totalPaginas)}>»</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Modal Beneficiarios ── */}
      {modalBenef && suscriptorActivo && (
        <div className="modal-overlay" onClick={() => setModalBenef(false)}>
          <div className="modal-benef" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom">
              <div>
                <h5 className="modal-title-custom"><FaUserFriends style={{ marginRight: 8 }} />Beneficiarios</h5>
                <span style={{ fontSize: "0.8rem", opacity: 0.8 }}>{suscriptorActivo.nombresApellidos} — {suscriptorActivo.nombre_plan}</span>
              </div>
              <button className="modal-close" onClick={() => setModalBenef(false)}><FaTimes size={14} /></button>
            </div>
            <div className="modal-body-custom" style={{ padding: "1.2rem 1.5rem", overflowX: "auto" }}>
              {loadingBenef ? (
                <div className="sin-resultados"><i className="fas fa-spinner fa-spin mr-2" /> Cargando beneficiarios...</div>
              ) : beneficiarios.length === 0 ? (
                <div className="sin-resultados">No hay beneficiarios registrados.</div>
              ) : (
                <table className="usuarios-table benef-table">
                  <thead>
                    <tr>
                      <th>#</th><th>Documento</th><th>Nombres</th><th>Email</th><th>Teléfono</th><th>Parentesco</th>
                    </tr>
                  </thead>
                  <tbody>
                    {beneficiarios.map((b, idx) => (
                      <tr key={b.id_beneficiario}>
                        <td className="td-id">{idx + 1}</td>
                        <td>
                          <span className="suscriptor-documento">
                            <FaIdCard size={11} style={{ marginRight: 4, opacity: 0.6 }} />
                            {b.tipo_documento} {b.numero_documento}
                          </span>
                        </td>
                        <td><span className="suscriptor-nombre">{[b.nombre1, b.nombre2, b.apellido1, b.apellido2].filter(Boolean).join(" ")}</span></td>
                        <td><span className="suscriptor-email">{b.email || "—"}</span></td>
                        <td className="td-secondary">{b.telefono || "—"}</td>
                        <td>
                          <span className={`badge-parentesco ${b.parentesco?.toLowerCase() === "titular" ? "badge-titular" : "badge-familiar"}`}>
                            {b.parentesco}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-footer-custom">
              <button className="btn-modal-cancelar" onClick={() => setModalBenef(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Documentos ── */}
      {modalDocs && suscDocActivo && (
        <div className="modal-overlay" onClick={() => setModalDocs(false)}>
          <div className="modal-benef" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-custom modal-header-docs">
              <div>
                <h5 className="modal-title-custom"><FaFileAlt style={{ marginRight: 8 }} />Documentos cargados</h5>
                <span style={{ fontSize: "0.8rem", opacity: 0.8 }}>{suscDocActivo.nombresApellidos}</span>
              </div>
              <button className="modal-close" onClick={() => setModalDocs(false)}><FaTimes size={14} /></button>
            </div>
            <div className="modal-body-custom" style={{ padding: "1.2rem 1.5rem", overflowX: "auto" }}>
              {loadingDocs ? (
                <div className="sin-resultados"><i className="fas fa-spinner fa-spin mr-2" /> Cargando documentos...</div>
              ) : documentos.length === 0 ? (
                <div className="sin-resultados">No hay documentos cargados.</div>
              ) : (
                <table className="usuarios-table benef-table">
                  <thead>
                    <tr>
                      <th>#</th><th>Nombre archivo</th><th>Fecha carga</th><th>Estado</th><th>Ver</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documentos.map((d, idx) => {
                      const partes = (d.url_documento || "").split("/");
                      const nombreRaw = partes[partes.length - 1] || "documento";
                      const nombreArchivo = nombreRaw.replace(/_[a-f0-9]{32}(\.[^.]+)$/, "$1");
                      const urlDescarga = d.id_documento
                        ? `${process.env.REACT_APP_API_BASE_URL}/api/Suscriptores/DescargarDocumento?id_documento=${d.id_documento}`
                        : null;
                      return (
                        <tr key={d.id_documento || idx}>
                          <td className="td-id">{idx + 1}</td>
                          <td>
                            <span className="suscriptor-nombre" title={d.url_documento}>
                              <FaFileAlt size={11} style={{ marginRight: 5, opacity: 0.5 }} />
                              {nombreArchivo}
                            </span>
                          </td>
                          <td className="td-secondary">
                            {d.fecha_registro
                              ? new Date(d.fecha_registro).toLocaleDateString("es-CO", { day: "2-digit", month: "short", year: "numeric" })
                              : "—"}
                          </td>
                          <td>
                            <span className={`badge-estado ${d.estado === "ACTIVO" ? "badge-activo" : "badge-inactivo"}`}>
                              <span className="badge-dot" />{d.estado}
                            </span>
                          </td>
                          <td>
                            {urlDescarga ? (
                              <a href={urlDescarga} target="_blank" rel="noreferrer" className="btn-ver-doc-link">
                                <FaFileAlt size={11} /> Ver
                              </a>
                            ) : (
                              <span className="td-secondary">Sin URL</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-footer-custom">
              <button className="btn-modal-cancelar" onClick={() => setModalDocs(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suscriptores;